import { supabase } from '@/lib/supabase'
import { ownerRepository } from '@/repositories/OwnerRepository'
import { IPaginatedRepositoryResponse } from '@/repositories/types'
import type { OwnerInsert, OwnerUpdate, OwnerWithPropertyCount } from '@/types'
import { useToast } from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useCRUD } from './useCRUD'
import { useData } from './useData'

export function useOwners(filters?: {
  search?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  page?: number
  limit?: number
  // owner_id?: string
  showDeleted?: boolean
}) {
  const toast = useToast()
  const { data, isLoading, error, refetch } = useData<OwnerWithPropertyCount>({
    repository: ownerRepository,
    fetcher: (repo) => repo.getAll(filters),
    queryKey: ['owners', JSON.stringify(filters)],
  })

  const {
    create,
    update,
    delete: softDelete,
    restore,
    isCreating,
    isUpdating,
    isDeleting,
    isRestoring,
  } = useCRUD<OwnerWithPropertyCount, OwnerInsert, OwnerUpdate>({
    repository: ownerRepository,
    queryKey: ['owner', 'owners', JSON.stringify(filters)],
    messages: {
      createSuccess: 'Proprietário criado com sucesso.',
      updateSuccess: 'Proprietário atualizado com sucesso.',
      deleteSuccess: 'Proprietário excluído com sucesso.',
      restoreSuccess: 'Proprietário restaurado com sucesso.',
    },
  })

  const sendVerificationMutation = useMutation({
    mutationFn: async (email: string) => {
      const { data, error } = await supabase.functions.invoke(
        'send-verification-email',
        {
          body: { email },
        }
      )

      if (error) throw error
      return data
    },
    onSuccess: () => {
      toast({
        title: 'Convite enviado com sucesso!',
        description:
          'O link de convite foi gerado. O proprietário poderá definir sua senha através deste link.',
        status: 'success',
        duration: 5000,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao enviar convite',
        description: error.message,
        status: 'error',
        duration: 5000,
      })
    },
  })

  // Type assertion to help TypeScript understand the paginated response structure
  const paginatedData = data as IPaginatedRepositoryResponse<
    OwnerWithPropertyCount,
    { totalLinkedProperties: number }
  > | null
  // Calculate statistics
  const statistics = useMemo(() => {
    if (
      !paginatedData ||
      !Array.isArray(paginatedData.items) ||
      paginatedData.items.length === 0
    ) {
      return {
        averagePropertiesPerOwner: 0,
      }
    }
    return {
      averagePropertiesPerOwner:
        ((paginatedData.items as OwnerWithPropertyCount[]) || []).length > 0
          ? ((paginatedData.items as OwnerWithPropertyCount[]) || []).reduce(
              (sum, owner) => sum + (owner.property_count || 0),
              0
            ) / ((paginatedData.items as OwnerWithPropertyCount[]) || []).length
          : 0,
    }
  }, [paginatedData])

  return {
    data: paginatedData?.items ?? [],
    count: paginatedData?.count ?? 0,
    totalPages: paginatedData?.totalPages ?? 0,
    currentPage: paginatedData?.currentPage ?? 1,
    stats: paginatedData?.stats ?? { totalLinkedProperties: 0 },
    isLoading,
    error,
    statistics,
    refetch,
    createOwner: create,
    updateOwner: update,
    softDelete,
    restore,
    sendVerification: sendVerificationMutation.mutate,
    isCreating,
    isUpdating,
    isDeleting,
    isSendingVerification: sendVerificationMutation.isPending,
    isRestoring,
  }
}
